import { useRef, useEffect } from 'react';
import logger from '../utils/logger';

// Centralized mapping for SFX files
const sfxPaths = {
    'ui_tap': '/sounds/sfx/ui_tap.mp3',
    'correct_answer': '/sounds/sfx/correct_answer.mp3',
    'incorrect_answer': '/sounds/sfx/incorrect_answer.mp3',
    'completion': '/sounds/sfx/completion.mp3',
    'game_over_fail': '/sounds/sfx/game_over_fail.mp3',
};

class AudioManager {
    constructor() {
        this.audioCtx = null;
        this.masterGain = null;
        this.bgmGain = null;
        this.sfxGain = null;
        this.isStarted = false;
        this.bgmTimer = null;
        this.currentBGMTrack = null;
        this.noteIndex = 0;
        this.settings = {
            isMusicEnabled: true,
            areLessonSoundsEnabled: true,
        };
        this.isSessionMuted = false;
        this.voicesLoaded = false;
        this.melodies = {
            dashboard: {
                notes: ['C4', 'E4', 'G4', 'B4', 'G4', 'E4'],
                noteDuration: 0.2,
                timeBetweenNotes: 0.5
            },
            storybook: {
                notes: ['A3', 'C4', 'E4', 'G4', 'E4', 'C4'],
                noteDuration: 0.4,
                timeBetweenNotes: 1.0
            }
        };
        this.noteToFreq = (note) => {
            const notes = { 'C3': 130.81, 'C#3': 138.59, 'A3': 220.00, 'C4': 261.63, 'E4': 329.63, 'G4': 392.00, 'B4': 493.88, 'C5': 523.25 };
            return notes[note] || 440;
        };
        
        // Load voices when available
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.onvoiceschanged = () => {
                this.voicesLoaded = true;
            };
            // Trigger voice loading
            window.speechSynthesis.getVoices();
        }
    }

    async start() {
        if (this.isStarted || typeof window === 'undefined') return;
        try {
            this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            if (this.audioCtx.state === 'suspended') {
                await this.audioCtx.resume();
            }
            this.masterGain = this.audioCtx.createGain();
            this.masterGain.connect(this.audioCtx.destination);
            this.bgmGain = this.audioCtx.createGain();
            this.bgmGain.gain.value = 0.05;
            this.bgmGain.connect(this.masterGain);
            this.sfxGain = this.audioCtx.createGain();
            this.sfxGain.gain.value = 0.1;
            this.sfxGain.connect(this.masterGain);
            this.isStarted = true;
        } catch (e) {
            logger.error('App', "Could not start AudioContext:", e);
        }
    }

    updateSettings(settings = {}) {
        const previousMusicSetting = this.settings.isMusicEnabled;
        this.settings = { ...this.settings, ...settings };
        if (!this.settings.isMusicEnabled) {
            this.stopBGM();
        } else if (this.settings.isMusicEnabled && !previousMusicSetting) {
            this.currentBGMTrack = null;
        }
    }

    setSessionMute(isMuted) {
        this.isSessionMuted = isMuted;
        if (this.masterGain && this.audioCtx) {
            this.masterGain.gain.setValueAtTime(isMuted ? 0 : 1, this.audioCtx.currentTime);
        }
    }

    playBGM(trackName) {
        if (!this.isStarted || !this.settings.isMusicEnabled || this.currentBGMTrack === trackName) return;
        this.stopBGM();
        this.currentBGMTrack = trackName;
        const melody = this.melodies[trackName];
        if (!melody) return;

        const scheduleNote = () => {
            if (this.currentBGMTrack !== trackName || !this.audioCtx) return;
            const note = melody.notes[this.noteIndex % melody.notes.length];
            const osc = this.audioCtx.createOscillator();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(this.noteToFreq(note), this.audioCtx.currentTime);
            osc.connect(this.bgmGain);
            osc.start();
            osc.stop(this.audioCtx.currentTime + melody.noteDuration);
            this.noteIndex++;
            this.bgmTimer = setTimeout(scheduleNote, melody.timeBetweenNotes * 1000);
        };
        scheduleNote();
    }

    stopBGM() {
        if (this.bgmTimer) {
            clearTimeout(this.bgmTimer);
            this.bgmTimer = null;
        }
        this.currentBGMTrack = null;
        this.noteIndex = 0;
    }

    // Corrected to play actual audio files instead of generating tones
    playSFX(sfxType) {
        if (!this.isStarted || this.isSessionMuted || !this.settings.areLessonSoundsEnabled) return;
        const sfxUrl = sfxPaths[sfxType];
        if (!sfxUrl) return;

        const audio = new Audio(sfxUrl);
        audio.volume = this.sfxGain.gain.value;
        audio.play().catch(e => logger.error('App', "SFX playback failed:", e));
    }

    speak(text, langCode, rate = 1.0) {
        return new Promise((resolve, reject) => {
            if (this.isSessionMuted || !this.settings.areLessonSoundsEnabled) {
                return reject(new Error("Muted"));
            }
            if (!('speechSynthesis' in window)) return reject(new Error("Speech Synthesis not supported."));
            
            const performSpeak = () => {
                // Check if voices are available for the requested language
                const voices = window.speechSynthesis.getVoices();
                
                // Find a voice that matches the requested language
                let selectedVoice = voices.find(voice => voice.lang === langCode);
                
                // If exact match not found, try partial match (e.g., "fr" for "fr-FR")
                if (!selectedVoice) {
                    const langPrefix = langCode.split('-')[0];
                    selectedVoice = voices.find(voice => voice.lang.startsWith(langPrefix));
                }
                
                // Firefox-specific: Try case-insensitive match (Firefox may have different casing)
                if (!selectedVoice) {
                    const langLower = langCode.toLowerCase();
                    selectedVoice = voices.find(voice => 
                        voice.lang.toLowerCase() === langLower || 
                        voice.lang.toLowerCase().startsWith(langLower.split('-')[0])
                    );
                }
                
                // If still no voice for French, try finding by name (Firefox stores voices differently)
                if (!selectedVoice && langCode.startsWith('fr')) {
                    selectedVoice = voices.find(voice => 
                        voice.lang.toLowerCase().includes('fr') || 
                        voice.name.toLowerCase().includes('french') ||
                        voice.name.toLowerCase().includes('français')
                    );
                }
                
                // Log voice selection result
                if (!selectedVoice && voices.length > 0) {
                    logger.warn('AudioManager', `Warning: No voice found for "${text}". Available:`, voices.slice(0, 5).map(v => `${v.name} (${v.lang})`));
                    logger.warn('AudioManager', 'Warning');
                    
                    // Firefox-specific help for French
                    if (langCode.startsWith('fr') && navigator.userAgent.includes('Firefox')) {
                        console.info(`%c💡 FIREFOX USERS: To enable French pronunciation:`, 'color: #0066cc; font-weight: bold; font-size: 14px; padding: 4px;');
                        console.info(`%c1. Open Firefox Settings → General → Language`, 'padding-left: 20px;');
                        console.info(`%c2. Click "Choose" → Add "French [fr]"`, 'padding-left: 20px;');
                        console.info(`%c3. Reload this page`, 'padding-left: 20px;');
                        console.info(`%cOr install TTS voices: https://support.mozilla.org/en-US/kb/text-speech`, 'color: #0066cc; padding: 4px;');
                    }
                } else if (selectedVoice) {
                    logger.info('AudioManager', `Voice found for "${text}"`);
                }
                
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = langCode;
                utterance.rate = rate;
                
                // Explicitly set the voice
                if (selectedVoice) {
                    utterance.voice = selectedVoice;
                }
                
                utterance.onend = resolve;
                utterance.onerror = (event) => {
                    if (event.error === 'interrupted') {
                        resolve();
                    } else {
                        logger.error('AudioManager', `Speech synthesis error for "${text}" (${langCode}):`, event.error);
                        reject(event.error);
                    }
                };
                window.speechSynthesis.cancel();
                window.speechSynthesis.speak(utterance);
            };
            
            // If voices aren't loaded yet, wait for them
            const voices = window.speechSynthesis.getVoices();
            if (voices.length === 0) {
                // Voices not loaded yet, wait for them
                const onVoicesChanged = () => {
                    window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
                    performSpeak();
                };
                window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
                // Also try triggering voice loading
                window.speechSynthesis.getVoices();
            } else {
                performSpeak();
            }
        });
    }

    stopAllSounds() {
        this.stopBGM();
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    }
}

export const useAudio = () => {
    // We use useRef to create a single instance of AudioManager
    // that persists across component re-renders.
    const audioManager = useRef(new AudioManager());

    // We can also add a cleanup effect here if needed.
    useEffect(() => {
      const currentAudioManager = audioManager.current;
      return () => {
        currentAudioManager.stopAllSounds();
      };
    }, []);

    return audioManager.current;
};

export { AudioManager };
